/**
 * База знаний компании (раздел 6.22, БЗ-07–11, БЗ-19, БЗ-20): статьи с версиями и проверкой владельцем, связи
 * статей с записями портала, поиск по словам, просмотр нормативного документа, подписки на изменения.
 * Деньги в разделе не участвуют.
 */
import { z } from 'zod';

const Id = z.uuid();
const DateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Разделы базы знаний (БЗ-07) */
export const KB_SECTIONS = {
  regulation: 'Регламенты и приказы',
  calc_rules: 'Правила расчётов',
  estimate_template: 'Шаблоны смет',
  typical: 'Типовые решения',
  lessons: 'Уроки проектов',
  support: 'Ответы поддержки',
  estimate_notes: 'Заметки сметного отдела',
} as const;
export type KbSection = keyof typeof KB_SECTIONS;
export const KbSectionSchema = z.enum(Object.keys(KB_SECTIONS) as [KbSection, ...KbSection[]]);

/** С чем связывается статья (БЗ-08) */
export const KB_LINK_TYPES = {
  project: 'Проект',
  activity: 'Работа графика',
  remark: 'Замечание',
  route: 'Маршрут согласования',
  work_type: 'Вид работ',
  contract_kind: 'Вид договора',
  norm_release: 'Документ нормативной базы',
  estimate: 'Смета',
  section: 'Раздел портала',
} as const;
export type KbLinkType = keyof typeof KB_LINK_TYPES;
export const KbLinkTypeSchema = z.enum(Object.keys(KB_LINK_TYPES) as [KbLinkType, ...KbLinkType[]]);

export const KB_VERSION_STATUSES = {
  draft: 'Черновик',
  review: 'На проверке',
  published: 'Действует',
  superseded: 'Заменена новой',
  rejected: 'Возвращена',
} as const;
export type KbVersionStatus = keyof typeof KB_VERSION_STATUSES;

export const KB_ARTICLE_STATUSES = {
  draft: 'Не опубликована',
  published: 'Опубликована',
  archived: 'В архиве',
} as const;
export type KbArticleStatus = keyof typeof KB_ARTICLE_STATUSES;

export const kbArticleNumber = (n: number) => `СТ-${String(n).padStart(4, '0')}`;

/** Часть текста, заменённая более поздней статьёй (БЗ-11): Pilot по такой части не отвечает */
export const KbReplacedPart = z.object({
  label: z.string().trim().min(1).max(300),
  replacedById: Id,
  since: DateStr,
});
export type KbReplacedPart = z.infer<typeof KbReplacedPart>;

const Roles = z.array(z.string().trim().min(1).max(60)).max(30);

export const KbArticleInput = z.object({
  section: KbSectionSchema,
  title: z.string().trim().min(3).max(300),
  summary: z.string().trim().max(1000).nullable().optional(),
  body: z.string().max(200_000).default(''),
  ownerId: Id.nullable().optional(),
  ownerUnit: z.string().trim().max(120).nullable().optional(),
  visibleRoles: Roles.default([]),
  reviewEveryMonths: z.number().int().min(1).max(60).default(12),
  projectId: Id.nullable().optional(),
});
export type KbArticleInput = z.infer<typeof KbArticleInput>;

/** Правка карточки статьи (владелец, видимость, срок пересмотра) — не текста */
export const KbArticlePatch = z.object({
  title: z.string().trim().min(3).max(300).optional(),
  section: KbSectionSchema.optional(),
  ownerId: Id.nullable().optional(),
  ownerUnit: z.string().trim().max(120).nullable().optional(),
  visibleRoles: Roles.optional(),
  reviewEveryMonths: z.number().int().min(1).max(60).optional(),
  projectId: Id.nullable().optional(),
});
export type KbArticlePatch = z.infer<typeof KbArticlePatch>;

export const KbVersionInput = z.object({
  title: z.string().trim().min(3).max(300),
  summary: z.string().trim().max(1000).nullable().optional(),
  body: z.string().max(200_000),
  changeNote: z.string().trim().max(1000).nullable().optional(),
  replacedParts: z.array(KbReplacedPart).max(50).default([]),
});
export type KbVersionInput = z.infer<typeof KbVersionInput>;

export const KbRejectInput = z.object({ comment: z.string().trim().min(3).max(1000) });
export const KbLinkInput = z.object({
  targetType: KbLinkTypeSchema,
  targetId: Id.nullable().optional(),
  targetKey: z.string().trim().min(1).max(120).nullable().optional(),
  label: z.string().trim().max(200).nullable().optional(),
});
export type KbLinkInput = z.infer<typeof KbLinkInput>;

export const KbLink = z.object({
  id: Id,
  targetType: KbLinkTypeSchema,
  targetId: Id.nullable(),
  targetKey: z.string().nullable(),
  label: z.string().nullable(),
  /** Куда вести из статьи; null — записи нет или она не доступна */
  path: z.string().nullable(),
});
export type KbLink = z.infer<typeof KbLink>;

export const KbVersionSummary = z.object({
  id: Id,
  version: z.number().int(),
  status: z.enum(['draft', 'review', 'published', 'superseded', 'rejected']),
  title: z.string(),
  changeNote: z.string().nullable(),
  authorId: Id.nullable(),
  authorName: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  reviewComment: z.string().nullable(),
  submittedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type KbVersionSummary = z.infer<typeof KbVersionSummary>;

export const KbVersionFull = KbVersionSummary.extend({
  summary: z.string().nullable(),
  body: z.string(),
  replacedParts: z.array(KbReplacedPart),
});
export type KbVersionFull = z.infer<typeof KbVersionFull>;

export const KbArticleItem = z.object({
  id: Id,
  number: z.string(),
  section: KbSectionSchema,
  title: z.string(),
  summary: z.string().nullable(),
  status: z.enum(['draft', 'published', 'archived']),
  ownerId: Id.nullable(),
  ownerName: z.string().nullable(),
  ownerUnit: z.string().nullable(),
  visibleRoles: z.array(z.string()),
  publishedVersion: z.number().int().nullable(),
  nextReviewAt: DateStr.nullable(),
  reviewOverdue: z.boolean(),
  /** Статья заменена целиком или есть заменённые части */
  replaced: z.boolean(),
  /** Есть версия, которая ждёт проверки */
  pendingReview: z.boolean(),
  projectId: Id.nullable(),
  updatedAt: z.string(),
});
export type KbArticleItem = z.infer<typeof KbArticleItem>;

export const KbArticleView = KbArticleItem.extend({
  reviewEveryMonths: z.number().int(),
  replacedById: Id.nullable(),
  replacedByNumber: z.string().nullable(),
  replacedByTitle: z.string().nullable(),
  replacedSince: DateStr.nullable(),
  sourceRef: z.string().nullable(),
  /** Опубликованная версия; null, если статья ещё не опубликована или читатель видит только свои черновики */
  published: KbVersionFull.nullable(),
  /** Версия в работе: черновик, на проверке или возвращённая */
  working: KbVersionFull.nullable(),
  versions: z.array(KbVersionSummary),
  links: z.array(KbLink),
  /** Названия статей, на которые указывают заменённые части */
  replacers: z.array(z.object({ id: Id, number: z.string(), title: z.string() })),
  canEdit: z.boolean(),
  canReview: z.boolean(),
  isOwner: z.boolean(),
});
export type KbArticleView = z.infer<typeof KbArticleView>;

export const KbListQuery = z.object({
  section: KbSectionSchema.optional(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
  mine: z.enum(['1', '0']).optional(),
  overdue: z.enum(['1', '0']).optional(),
  q: z.string().trim().max(200).optional(),
});

export const KbQueue = z.object({
  /** Версии, которые ждут моей проверки */
  toReview: z.array(
    KbArticleItem.extend({
      versionId: Id,
      version: z.number().int(),
      authorName: z.string().nullable(),
      submittedAt: z.string().nullable(),
    }),
  ),
  /** Мои черновики и возвращённые версии */
  mine: z.array(
    KbArticleItem.extend({
      versionId: Id,
      version: z.number().int(),
      versionStatus: z.enum(['draft', 'rejected']),
      reviewComment: z.string().nullable(),
    }),
  ),
  /** Статьи, у которых вышел срок пересмотра и я владелец */
  dueReview: z.array(KbArticleItem),
});
export type KbQueue = z.infer<typeof KbQueue>;

export const KbSearchHit = z.object({
  kind: z.enum(['article', 'norm']),
  id: Id,
  title: z.string(),
  /** Номер статьи или обозначение документа */
  code: z.string(),
  section: z.string().nullable(),
  version: z.number().int().nullable(),
  /** Фрагмент: найденные слова обрамлены знаками ⟦ и ⟧ */
  snippet: z.string(),
  status: z.string().nullable(),
  path: z.string(),
  rank: z.number(),
});
export type KbSearchHit = z.infer<typeof KbSearchHit>;

export const KbSearchResult = z.object({
  query: z.string(),
  hits: z.array(KbSearchHit),
  /** Поиск по смыслу не подключён, пока нет Pilot (ИИ-06) */
  semantic: z.boolean(),
});
export type KbSearchResult = z.infer<typeof KbSearchResult>;

export const KbRelated = z.object({
  articles: z.array(KbArticleItem),
});
export type KbRelated = z.infer<typeof KbRelated>;

/** Подписки (БЗ-20). Темы: norm_all, norm_kind:<вид>, kb_section:<раздел> */
export const KbSubscriptionInput = z.object({
  topics: z.array(z.string().trim().min(3).max(80)).max(60),
});
export const KbSubscriptions = z.object({
  topics: z.array(z.string()),
  /** Что предлагается подписать по роли человека */
  suggested: z.array(z.object({ topic: z.string(), title: z.string() })),
});
export type KbSubscriptions = z.infer<typeof KbSubscriptions>;

export const KbEvent = z.object({
  id: Id,
  topic: z.string(),
  kind: z.enum(['norm_release_new', 'norm_status_changed', 'article_published', 'article_review_due']),
  title: z.string(),
  path: z.string(),
  read: z.boolean(),
  createdAt: z.string(),
});
export type KbEvent = z.infer<typeof KbEvent>;

/** Просмотр нормативного документа (БЗ-19) */
export const KbNormToc = z.object({
  /** Номер пункта («4.2») или «Приложение А» */
  n: z.string(),
  title: z.string(),
  page: z.number().int().nullable(),
  level: z.number().int(),
});
export const KbNormDoc = z.object({
  releaseId: Id,
  code: z.string(),
  name: z.string(),
  nameKk: z.string().nullable(),
  status: z.string(),
  languages: z.array(z.string()),
  /** Файл документа и язык, который показан */
  fileId: Id.nullable(),
  lang: z.string().nullable(),
  /** ready — текст есть; no_text_layer — скан, нужно распознавание; no_file — файла нет; unreadable — не прочитан */
  textState: z.enum(['ready', 'no_text_layer', 'no_file', 'unreadable']),
  ocrAvailable: z.boolean(),
  ocrNote: z.string().nullable(),
  pages: z.number().int(),
  paged: z.boolean(),
  toc: z.array(KbNormToc),
});
export type KbNormDoc = z.infer<typeof KbNormDoc>;
export const KbNormPage = z.object({ page: z.number().int(), text: z.string() });
export const KbNormPages = z.object({ pages: z.array(KbNormPage), total: z.number().int() });
export const KbNormHit = z.object({
  page: z.number().int(),
  n: z.string().nullable(),
  snippet: z.string(),
});

export const KbImportResult = z.object({ articleId: Id, number: z.string(), versionId: Id });
